import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  MapPin,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Send,
  Info,
  X,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Paperclip,
  Navigation,
  Sparkles,
  Search,
  RefreshCw,
  Landmark,
  CreditCard,
  DollarSign,
  FileText,
  Upload,
  FileSpreadsheet,
  Layers,
  UserCheck,
  ChevronRight,
  TrendingUp,
  Receipt,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGES = {
  SUBMITTED: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  FINANCE_REVIEW: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  APPROVED: { label: 'Payment Pending', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  PAYMENT_PENDING: { label: 'Payment Pending', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  PARTIALLY_PAID: { label: 'Partially Paid', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  PAID: { label: 'Paid / Disbursed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  REJECTED: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  DRAFT: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const StatusBadge = ({ status }) => {
  const normStatus = (status || 'SUBMITTED').toUpperCase();
  const meta = STATUS_BADGES[normStatus] || { label: normStatus, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

export default function FinanceLocalTravelReview() {
  const [activeView, setActiveView] = useState('expense'); // 'expense' or 'employee'
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [travelMode, setTravelMode] = useState('all');
  const [search, setSearch] = useState('');

  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    total_submitted: 0,
    pending_finance_review: 0,
    approved_count: 0,
    rejected_count: 0,
    payment_pending_count: 0,
    paid_count: 0,
    total_claimed_amount: 0,
    total_approved_amount: 0,
    total_paid_amount: 0,
    balance_payable: 0,
  });

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals State
  const [viewingReport, setViewingReport] = useState(null);
  const [viewingEmployeeDetails, setViewingEmployeeDetails] = useState(null);
  const [approveDialog, setApproveDialog] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState('');

  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const [payDialog, setPayDialog] = useState(null);
  const [paymentData, setPaymentData] = useState({
    paid_date: new Date().toISOString().slice(0, 10),
    payment_mode: 'Bank Transfer',
    transaction_ref: '',
    paid_amount: 0,
    payment_remarks: '',
  });

  // Bulk Upload Modal State
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Fetch Companies
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/companies/public`, { withCredentials: true }).catch(() =>
          axios.get(`${API_URL}/companies`, { withCredentials: true })
        );
        const compList = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setCompanies(compList);
      } catch {
        // Fallback
      }
    })();
  }, []);

  // Fetch Reports List
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/ess/local-travel/finance/review-list?status=${statusFilter}&company_id=${selectedCompany}&travel_mode=${travelMode}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const { data } = await axios.get(url, { withCredentials: true });
      setReports(data.reports || []);
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      toast.error('Failed to load Local Conveyance records for Finance Review');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, statusFilter, fromDate, toDate, travelMode, search]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Compute Employee-wise Consolidated Summary
  const employeeConsolidated = useMemo(() => {
    const map = {};
    reports.forEach((r) => {
      const key = r.employee_code || r.employee_id || 'UNKNOWN';
      if (!map[key]) {
        map[key] = {
          employee_id: r.employee_id,
          employee_code: r.employee_code || 'N/A',
          employee_name: r.employee_name || 'Employee',
          department_name: r.department_name || 'General',
          company_name: r.company_name || 'Primary Company',
          branch_name: r.branch_name || 'Main Branch',
          total_count: 0,
          total_claimed: 0,
          total_approved: 0,
          total_paid: 0,
          total_rejected: 0,
          total_pending_payment: 0,
          statuses: new Set(),
          reports: [],
        };
      }
      const st = (r.status || 'SUBMITTED').toUpperCase();
      const gTotal = Number(r.total_amount || 0);
      const eligTotal = Number(r.eligible_amount || gTotal);
      const paidAmt = Number(r.paid_amount || (st === 'PAID' ? gTotal : 0));

      map[key].total_count += 1;
      map[key].total_claimed += gTotal;
      map[key].statuses.add(st);
      map[key].reports.push(r);

      if (['APPROVED', 'PAYMENT_PENDING', 'PAID'].includes(st)) {
        map[key].total_approved += eligTotal;
      }
      if (st === 'PAID') {
        map[key].total_paid += paidAmt;
      } else if (['APPROVED', 'PAYMENT_PENDING'].includes(st)) {
        map[key].total_pending_payment += eligTotal;
      } else if (st === 'REJECTED') {
        map[key].total_rejected += gTotal;
      }
    });

    return Object.values(map).map((emp) => {
      const balancePayable = Math.max(0, emp.total_approved - emp.total_paid);
      let paymentStatus = 'SUBMITTED';
      const stArr = Array.from(emp.statuses);

      if (emp.total_paid >= emp.total_approved && emp.total_approved > 0) {
        paymentStatus = 'PAID';
      } else if (emp.total_paid > 0) {
        paymentStatus = 'PARTIALLY_PAID';
      } else if (stArr.includes('PAYMENT_PENDING') || stArr.includes('APPROVED')) {
        paymentStatus = 'PAYMENT_PENDING';
      } else if (stArr.includes('SUBMITTED') || stArr.includes('FINANCE_REVIEW')) {
        paymentStatus = 'SUBMITTED';
      } else if (stArr.includes('REJECTED')) {
        paymentStatus = 'REJECTED';
      }

      return {
        ...emp,
        balance_payable: balancePayable,
        payment_status: paymentStatus,
      };
    });
  }, [reports]);

  // Handle Approve Confirm
  const handleApproveConfirm = async () => {
    if (!approveDialog) return;
    try {
      setActionLoading(true);
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${approveDialog.id}/approve`,
        { approval_remarks: approvalRemarks },
        { withCredentials: true }
      );
      toast.success('Local Conveyance claim APPROVED and moved to Payment Pending!');
      setApproveDialog(null);
      setApprovalRemarks('');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve report');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject Confirm
  const handleRejectConfirm = async () => {
    if (!rejectDialog) return;
    if (!rejectionReason.trim()) {
      toast.error('Please enter a mandatory rejection reason.');
      return;
    }
    try {
      setActionLoading(true);
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${rejectDialog.id}/reject`,
        { rejection_reason: rejectionReason },
        { withCredentials: true }
      );
      toast.success('Local Conveyance claim REJECTED.');
      setRejectDialog(null);
      setRejectionReason('');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject report');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Pay Confirm
  const handlePayConfirm = async () => {
    if (!payDialog) return;
    if (!paymentData.transaction_ref.trim()) {
      toast.error('Please enter Transaction / Reference Number.');
      return;
    }
    try {
      setActionLoading(true);
      await axios.post(
        `${API_URL}/ess/local-travel/finance/${payDialog.id}/pay`,
        paymentData,
        { withCredentials: true }
      );
      toast.success('Payment completed and status updated to PAID!');
      setPayDialog(null);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Open Pay Modal
  const openPayModal = (report) => {
    const amt = report.eligible_amount || report.total_amount || (report.totals && report.totals.grand_total) || 0;
    setPaymentData({
      paid_date: new Date().toISOString().slice(0, 10),
      payment_mode: 'Bank Transfer',
      transaction_ref: '',
      paid_amount: amt,
      payment_remarks: 'Processed by Finance',
    });
    setPayDialog(report);
  };

  // Export Data Handler
  const handleExport = (exportFormat = 'excel') => {
    let url = `${API_URL}/ess/local-travel/finance/export?view_type=${activeView}&format=${exportFormat}&company_id=${selectedCompany}&status=${statusFilter}&travel_mode=${travelMode}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    window.open(url, '_blank');
  };

  // Download Template
  const handleDownloadBulkTemplate = () => {
    window.open(`${API_URL}/ess/local-travel/finance/bulk-template`, '_blank');
  };

  // Process Bulk Upload
  const handleProcessBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please select an Excel or CSV file to upload.');
      return;
    }
    try {
      setBulkUploading(true);
      const formData = new FormData();
      formData.append('file', bulkFile);
      const { data } = await axios.post(
        `${API_URL}/ess/local-travel/finance/bulk-upload`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setBulkResult(data);
      if (data.successfully_created > 0) {
        toast.success(`Successfully created ${data.successfully_created} Local Conveyance claims!`);
        fetchReports();
      } else {
        toast.warning(data.message || 'No records created.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process bulk upload.');
    } finally {
      setBulkUploading(false);
    }
  };

  // Download PDF
  const downloadPdf = async (repId) => {
    try {
      const res = await axios.get(`${API_URL}/ess/local-travel/${repId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LocalConveyance_${repId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans" data-testid="finance-local-travel-page">
        
        {/* TOP HEADER BAR */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30 shrink-0">
              <Landmark className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Finance — Local Conveyance Review</h1>
              <p className="text-xs text-blue-200 mt-0.5">
                Dual summary views, line-item audits, bulk uploads, employee balances &amp; disbursement processing.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* VIEW MODE TOGGLE TABS */}
            <div className="bg-slate-800/90 p-1 rounded-xl border border-slate-700 flex items-center gap-1 shadow-inner">
              <button
                type="button"
                onClick={() => setActiveView('expense')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeView === 'expense'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-slate-300 hover:text-white hover:bg-slate-700/60'
                }`}
                title="Expense-wise Line Item View"
              >
                <Layers className="w-3.5 h-3.5" /> Expense Line Items
              </button>
              <button
                type="button"
                onClick={() => setActiveView('employee')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeView === 'employee'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-slate-300 hover:text-white hover:bg-slate-700/60'
                }`}
                title="Employee-wise Consolidated View"
              >
                <Users className="w-3.5 h-3.5" /> Employee Summary
              </button>
            </div>

            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="bg-slate-800 text-white border border-slate-700 rounded-xl px-3 py-2 text-xs font-bold outline-none cursor-pointer"
              data-testid="select-company-filter"
            >
              <option value="all">All Companies</option>
              {companies.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name || c.company_name}</option>
              ))}
            </select>

            <button
              onClick={() => handleExport('excel')}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Export to Excel"
              data-testid="btn-export-excel"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </button>

            <button
              onClick={() => handleExport('csv')}
              className="px-3 py-2 bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Export to CSV"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>

            <button
              onClick={() => setShowBulkModal(true)}
              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition-all"
              title="Bulk Upload Claims"
            >
              <Upload className="w-3.5 h-3.5" /> Bulk Upload
            </button>

            <Button onClick={fetchReports} variant="outline" className="text-xs border-slate-700 bg-slate-800 text-white hover:bg-slate-700">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* 10 SUMMARY KPI CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5 text-xs" data-testid="finance-summary-cards">
          <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Submitted</span>
            <p className="text-lg font-black text-slate-800">{summary.total_submitted}</p>
          </div>
          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-amber-700 uppercase tracking-wider block">Pending Review</span>
            <p className="text-lg font-black text-amber-800">{summary.pending_finance_review}</p>
          </div>
          <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-blue-700 uppercase tracking-wider block">Approved</span>
            <p className="text-lg font-black text-blue-800">{summary.approved_count}</p>
          </div>
          <div className="bg-rose-50/80 border border-rose-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-rose-700 uppercase tracking-wider block">Rejected</span>
            <p className="text-lg font-black text-rose-800">{summary.rejected_count}</p>
          </div>
          <div className="bg-amber-100/60 border border-amber-300 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-amber-900 uppercase tracking-wider block">Pay Pending</span>
            <p className="text-lg font-black text-amber-950">{summary.payment_pending_count}</p>
          </div>
          <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-wider block">Paid / Disbursed</span>
            <p className="text-lg font-black text-emerald-800">{summary.paid_count}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Total Claimed (₹)</span>
            <p className="text-sm font-black text-slate-900 font-mono">₹ {(summary.total_claimed_amount || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-indigo-700 uppercase tracking-wider block">Total Approved (₹)</span>
            <p className="text-sm font-black text-indigo-900 font-mono">₹ {(summary.total_approved_amount || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-emerald-100/60 border border-emerald-300 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-emerald-900 uppercase tracking-wider block">Total Paid (₹)</span>
            <p className="text-sm font-black text-emerald-900 font-mono">₹ {(summary.total_paid_amount || 0).toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-amber-100 border border-amber-300 rounded-xl p-3 shadow-sm space-y-0.5">
            <span className="text-[9px] font-bold text-amber-950 uppercase tracking-wider block">Net Payable (₹)</span>
            <p className="text-sm font-black text-amber-900 font-mono">₹ {(summary.balance_payable || 0).toLocaleString('en-IN')}</p>
          </div>
        </div>

        {/* FILTERS BAR */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3 text-xs" data-testid="finance-filters">
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 flex-1 min-w-[220px]">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                type="text"
                placeholder="Search employee, code, purpose, location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent outline-none text-xs w-full"
                data-testid="input-search"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 bg-white font-semibold outline-none cursor-pointer"
              data-testid="select-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="SUBMITTED">Pending Review</option>
              <option value="PAYMENT_PENDING">Payment Pending</option>
              <option value="PAID">Paid / Disbursed</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <select
              value={travelMode}
              onChange={(e) => setTravelMode(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 bg-white font-semibold outline-none cursor-pointer"
              data-testid="select-mode-filter"
            >
              <option value="all">All Travel Modes</option>
              <option value="Cab">Cab / Taxi</option>
              <option value="Auto">Auto</option>
              <option value="Own Two-Wheeler">Own Two-Wheeler</option>
              <option value="Own Car">Own Car</option>
              <option value="Public Transport">Public Transport</option>
              <option value="Train">Train</option>
              <option value="Bus">Bus</option>
            </select>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <span className="text-slate-400 text-[11px] font-medium">Date Range:</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-slate-300 rounded-xl px-2.5 py-1 text-xs outline-none"
              data-testid="input-from-date"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-slate-300 rounded-xl px-2.5 py-1 text-xs outline-none"
              data-testid="input-to-date"
            />
          </div>
        </div>

        {/* DUAL VIEW TABLE DATA CONTAINER */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
          
          {/* VIEW MODE 1: EXPENSE LINE ITEMS VIEW */}
          {activeView === 'expense' && (
            <div className="overflow-x-auto" data-testid="table-expense-view">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs italic">
                  No Local Conveyance line item records found for Finance Review.
                </div>
              ) : (
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2.5">Date</th>
                      <th className="px-3 py-2.5">Employee</th>
                      <th className="px-3 py-2.5">Company / Branch</th>
                      <th className="px-3 py-2.5">Purpose &amp; Route</th>
                      <th className="px-3 py-2.5">Mode &amp; KM</th>
                      <th className="px-3 py-2.5 text-right">Claimed (₹)</th>
                      <th className="px-3 py-2.5 text-right">Approved (₹)</th>
                      <th className="px-3 py-2.5 text-right">Paid (₹)</th>
                      <th className="px-3 py-2.5 text-center">Status</th>
                      <th className="px-3 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reports.map((r) => {
                      const isSubmitted = r.status === 'SUBMITTED' || r.status === 'FINANCE_REVIEW';
                      const isPaymentPending = r.status === 'PAYMENT_PENDING' || r.status === 'APPROVED';
                      const isPaid = r.status === 'PAID';

                      return (
                        <tr key={r.id} className="hover:bg-slate-50/80 transition" data-testid={`finance-row-${r.id}`}>
                          <td className="px-3 py-3 font-bold text-slate-800 font-mono whitespace-nowrap">{r.report_date}</td>
                          <td className="px-3 py-3">
                            <p className="font-bold text-slate-900">{r.beneficiary_name || r.employee_name}</p>
                            <p className="text-[10px] text-slate-500 font-mono">{r.beneficiary_employee_code || r.employee_code} &middot; {r.department_name}</p>
                            {r.requested_on_behalf && (
                              <span className="text-[9px] bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.5 rounded border border-indigo-200 mt-0.5 inline-block">
                                Req by: {r.requester_name || r.employee_name}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-semibold text-slate-800">{r.company_name}</p>
                            <p className="text-[10px] text-slate-500">{r.branch_name}</p>
                          </td>
                          <td className="px-3 py-3 max-w-xs">
                            <p className="font-semibold text-slate-800 truncate" title={r.travel_purpose}>{r.travel_purpose}</p>
                            <p className="text-[10px] text-slate-500 truncate">{r.location_summary}</p>
                          </td>
                          <td className="px-3 py-3 text-slate-700 font-medium whitespace-nowrap">
                            <p>{r.travel_mode}</p>
                            <p className="text-[10px] text-slate-400 font-mono">{r.total_km || 0} KM</p>
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-bold text-slate-800 whitespace-nowrap">
                            ₹ {(r.total_amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-bold text-indigo-700 whitespace-nowrap">
                            ₹ {(r.eligible_amount || r.total_amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-black text-emerald-700 whitespace-nowrap">
                            {isPaid ? `₹ ${(r.paid_amount || r.total_amount || 0).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td className="px-3 py-3 text-center whitespace-nowrap">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => setViewingReport(r)}
                                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg flex items-center gap-1"
                                title="View full claim breakdown"
                                data-testid={`btn-view-${r.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" /> View
                              </button>

                              {isSubmitted && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setApproveDialog(r)}
                                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-sm"
                                    data-testid={`btn-approve-${r.id}`}
                                  >
                                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setRejectDialog(r)}
                                    className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-sm"
                                    data-testid={`btn-reject-${r.id}`}
                                  >
                                    <XCircle className="w-3.5 h-3.5" /> Reject
                                  </button>
                                </>
                              )}

                              {isPaymentPending && (
                                <button
                                  type="button"
                                  onClick={() => openPayModal(r)}
                                  className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-sm"
                                  data-testid={`btn-pay-${r.id}`}
                                >
                                  <CreditCard className="w-3.5 h-3.5" /> Mark Paid
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => downloadPdf(r.id)}
                                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold rounded-lg"
                                title="Download PDF"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* VIEW MODE 2: EMPLOYEE-WISE CONSOLIDATED SUMMARY VIEW */}
          {activeView === 'employee' && (
            <div className="overflow-x-auto" data-testid="table-employee-view">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : employeeConsolidated.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs italic">
                  No consolidated employee Local Conveyance records found.
                </div>
              ) : (
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-900 text-white text-[11px] uppercase font-bold border-b border-slate-800">
                    <tr>
                      <th className="px-3.5 py-3">Employee Details</th>
                      <th className="px-3.5 py-3">Company &amp; Dept</th>
                      <th className="px-3.5 py-3 text-center">Expenses Count</th>
                      <th className="px-3.5 py-3 text-right">Total Claimed (₹)</th>
                      <th className="px-3.5 py-3 text-right">Total Approved (₹)</th>
                      <th className="px-3.5 py-3 text-right">Total Paid (₹)</th>
                      <th className="px-3.5 py-3 text-right">Balance Payable (₹)</th>
                      <th className="px-3.5 py-3 text-center">Payment Status</th>
                      <th className="px-3.5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {employeeConsolidated.map((emp) => (
                      <tr key={emp.employee_code} className="hover:bg-slate-50/90 transition">
                        <td className="px-3.5 py-3">
                          <p className="font-bold text-slate-900 text-xs">{emp.employee_name}</p>
                          <p className="text-[10px] text-slate-500 font-mono">Code: {emp.employee_code}</p>
                        </td>

                        <td className="px-3.5 py-3">
                          <p className="font-semibold text-slate-800">{emp.company_name}</p>
                          <p className="text-[10px] text-slate-500">{emp.department_name} &middot; {emp.branch_name}</p>
                        </td>

                        <td className="px-3.5 py-3 text-center">
                          <span className="px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 font-bold text-slate-800 font-mono">
                            {emp.total_count} claims
                          </span>
                        </td>

                        <td className="px-3.5 py-3 text-right font-mono font-bold text-slate-900">
                          ₹ {(emp.total_claimed || 0).toLocaleString('en-IN')}
                        </td>

                        <td className="px-3.5 py-3 text-right font-mono font-bold text-indigo-700">
                          ₹ {(emp.total_approved || 0).toLocaleString('en-IN')}
                        </td>

                        <td className="px-3.5 py-3 text-right font-mono font-black text-emerald-700">
                          ₹ {(emp.total_paid || 0).toLocaleString('en-IN')}
                        </td>

                        <td className="px-3.5 py-3 text-right font-mono font-black text-amber-800">
                          <span className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200">
                            ₹ {(emp.balance_payable || 0).toLocaleString('en-IN')}
                          </span>
                        </td>

                        <td className="px-3.5 py-3 text-center">
                          <StatusBadge status={emp.payment_status} />
                        </td>

                        <td className="px-3.5 py-3 text-right whitespace-nowrap">
                          <Button
                            onClick={() => setViewingEmployeeDetails(emp)}
                            className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-3 py-1 rounded-xl shadow-sm flex items-center gap-1 ml-auto"
                          >
                            <Eye className="w-3.5 h-3.5" /> View Details ({emp.total_count})
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* EMPLOYEE CONSOLIDATED DETAILS MODAL */}
        {viewingEmployeeDetails && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 p-6 border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center text-blue-700 font-bold text-base">
                    {(viewingEmployeeDetails.employee_name || 'E').charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      {viewingEmployeeDetails.employee_name} ({viewingEmployeeDetails.employee_code})
                    </h3>
                    <p className="text-xs text-slate-500">
                      {viewingEmployeeDetails.department_name} &middot; {viewingEmployeeDetails.company_name} ({viewingEmployeeDetails.branch_name})
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setViewingEmployeeDetails(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Employee Summary Banner */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 bg-slate-900 text-white p-4 rounded-xl text-xs font-mono">
                <div><span className="text-slate-400 block text-[10px] uppercase font-sans">Total Claims</span><b>{viewingEmployeeDetails.total_count} records</b></div>
                <div><span className="text-slate-400 block text-[10px] uppercase font-sans">Total Claimed</span><b>₹ {(viewingEmployeeDetails.total_claimed || 0).toLocaleString('en-IN')}</b></div>
                <div><span className="text-indigo-300 block text-[10px] uppercase font-sans">Total Approved</span><b className="text-indigo-300">₹ {(viewingEmployeeDetails.total_approved || 0).toLocaleString('en-IN')}</b></div>
                <div><span className="text-emerald-400 block text-[10px] uppercase font-sans">Total Paid</span><b className="text-emerald-400">₹ {(viewingEmployeeDetails.total_paid || 0).toLocaleString('en-IN')}</b></div>
                <div><span className="text-amber-400 block text-[10px] uppercase font-sans">Balance Payable</span><b className="text-amber-400">₹ {(viewingEmployeeDetails.balance_payable || 0).toLocaleString('en-IN')}</b></div>
              </div>

              {/* Expense Table for this Employee */}
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-100 font-bold text-slate-700 border-b">
                    <tr>
                      <th className="px-3 py-2.5">Date</th>
                      <th className="px-3 py-2.5">Purpose &amp; Route</th>
                      <th className="px-3 py-2.5">Mode</th>
                      <th className="px-3 py-2.5 text-right">Claimed (₹)</th>
                      <th className="px-3 py-2.5 text-right">Approved (₹)</th>
                      <th className="px-3 py-2.5 text-right">Paid (₹)</th>
                      <th className="px-3 py-2.5 text-center">Status</th>
                      <th className="px-3 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {viewingEmployeeDetails.reports.map((r) => {
                      const isSubmitted = r.status === 'SUBMITTED' || r.status === 'FINANCE_REVIEW';
                      const isPaymentPending = r.status === 'PAYMENT_PENDING' || r.status === 'APPROVED';
                      const isPaid = r.status === 'PAID';

                      return (
                        <tr key={r.id} className="hover:bg-slate-50 transition">
                          <td className="px-3 py-2.5 font-bold font-mono text-slate-800 whitespace-nowrap">{r.report_date}</td>
                          <td className="px-3 py-2.5 max-w-xs">
                            <p className="font-semibold text-slate-800 truncate">{r.travel_purpose}</p>
                            <p className="text-[10px] text-slate-500 truncate">{r.location_summary}</p>
                          </td>
                          <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{r.travel_mode}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">
                            ₹ {(r.total_amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono font-bold text-indigo-700">
                            ₹ {(r.eligible_amount || r.total_amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono font-black text-emerald-700">
                            {isPaid ? `₹ ${(r.paid_amount || r.total_amount || 0).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => setViewingReport(r)}
                                className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded"
                              >
                                View
                              </button>
                              {isSubmitted && (
                                <button
                                  type="button"
                                  onClick={() => setApproveDialog(r)}
                                  className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded"
                                >
                                  Approve
                                </button>
                              )}
                              {isPaymentPending && (
                                <button
                                  type="button"
                                  onClick={() => openPayModal(r)}
                                  className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded"
                                >
                                  Pay
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setViewingEmployeeDetails(null)}>
                  Close Summary
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* BULK UPLOAD MODAL */}
        {showBulkModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-5 border border-slate-200">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-indigo-100 text-indigo-700 rounded-xl">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">Bulk Upload Local Conveyance</h3>
                    <p className="text-xs text-slate-500">Upload Excel (.xlsx) or CSV containing daily conveyance expense entries.</p>
                  </div>
                </div>
                <button type="button" onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Instructions */}
              <div className="bg-indigo-50/80 p-4 rounded-xl border border-indigo-200 space-y-2 text-xs text-indigo-950">
                <div className="flex items-center justify-between font-bold text-indigo-900">
                  <span>Excel Template Instructions:</span>
                  <button
                    type="button"
                    onClick={handleDownloadBulkTemplate}
                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs flex items-center gap-1 shadow-sm"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Sample Template
                  </button>
                </div>
                <ul className="list-disc list-inside space-y-1 text-slate-700 text-[11px]">
                  <li>Mandatory columns: <b>Employee Code *</b>, <b>Travel Date (YYYY-MM-DD) *</b>, <b>From Location *</b>, <b>To Location *</b>, <b>Purpose *</b>, <b>Conveyance Amount (₹) *</b>.</li>
                  <li>Only <b>ONE daily report</b> per employee is permitted per date. Duplicates will be flagged with row errors.</li>
                  <li>Row validation is performed prior to saving. Invalid employee codes or formatted dates will be reported.</li>
                </ul>
              </div>

              {/* File Selector */}
              <div className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-6 text-center bg-slate-50 transition cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="hidden"
                  id="local-bulk-input"
                />
                <label htmlFor="local-bulk-input" className="cursor-pointer block space-y-2">
                  <FileSpreadsheet className="w-10 h-10 text-indigo-600 mx-auto" />
                  <span className="font-bold text-slate-800 text-xs block">
                    {bulkFile ? bulkFile.name : 'Click to browse or drop Local Conveyance file (.xlsx, .csv)'}
                  </span>
                  <span className="text-[10px] text-slate-400 block">Maximum file size: 10 MB</span>
                </label>
              </div>

              {/* Results Banner if uploaded */}
              {bulkResult && (
                <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs">
                  <div className="flex items-center justify-between font-bold">
                    <span className="text-slate-800">Batch Upload Results Summary:</span>
                    <div className="flex gap-2 text-xs">
                      <span className="px-2.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                        {bulkResult.successfully_created} Created
                      </span>
                      <span className="px-2.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300">
                        {bulkResult.failed_count} Failed
                      </span>
                    </div>
                  </div>

                  {bulkResult.errors && bulkResult.errors.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-rose-200 rounded-lg bg-white p-2 space-y-1">
                      <span className="text-[10px] font-bold text-rose-800 uppercase block">Row Error Log Details:</span>
                      {bulkResult.errors.map((err, i) => (
                        <div key={i} className="text-[11px] text-rose-700 flex justify-between border-b border-rose-100 pb-0.5">
                          <span>Row #{err.row} ({err.employee_code}): {err.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setShowBulkModal(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleProcessBulkUpload}
                  disabled={bulkUploading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                >
                  {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Process Upload
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* APPROVE DIALOG */}
        {approveDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="approve-modal">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 border border-slate-200 space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" /> Approve Local Conveyance
                </h3>
                <button type="button" onClick={() => setApproveDialog(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 text-xs text-emerald-900 space-y-1">
                <p><b>Employee:</b> {approveDialog.employee_name} ({approveDialog.employee_code})</p>
                <p><b>Report Date:</b> {approveDialog.report_date}</p>
                <p><b>Total Claim Amount:</b> <span className="font-bold text-emerald-700 font-mono text-sm">₹ {(approveDialog.total_amount || 0).toLocaleString('en-IN')}</span></p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Approval Remarks (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="Verified receipts & approved for payment..."
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl p-2.5 text-xs outline-none focus:border-emerald-500"
                  data-testid="input-approval-remarks"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setApproveDialog(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleApproveConfirm}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid="btn-confirm-approve"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Confirm Approval
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* REJECT DIALOG */}
        {rejectDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="reject-modal">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 border border-slate-200 space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-rose-600" /> Reject Local Conveyance
                </h3>
                <button type="button" onClick={() => setRejectDialog(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-rose-50 p-3 rounded-xl border border-rose-200 text-xs text-rose-900 space-y-1">
                <p><b>Employee:</b> {rejectDialog.employee_name} ({rejectDialog.employee_code})</p>
                <p><b>Claim Amount:</b> ₹ {(rejectDialog.total_amount || 0).toLocaleString('en-IN')}</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Rejection Reason * (Mandatory)</label>
                <textarea
                  rows={3}
                  placeholder="State the reason why this claim is rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl p-2.5 text-xs outline-none focus:border-rose-500"
                  data-testid="input-rejection-reason"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setRejectDialog(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleRejectConfirm}
                  disabled={actionLoading}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
                  data-testid="btn-confirm-reject"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* PAY DIALOG */}
        {payDialog && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="pay-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 border border-slate-200 space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" /> Mark Payment as Paid
                </h3>
                <button type="button" onClick={() => setPayDialog(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-blue-50 p-3 rounded-xl border border-blue-200 text-xs text-blue-900 space-y-1">
                <p><b>Employee:</b> {payDialog.employee_name} ({payDialog.employee_code})</p>
                <p><b>Company / Branch:</b> {payDialog.company_name} - {payDialog.branch_name}</p>
                <p><b>Approved Claim Amount:</b> <span className="font-bold text-blue-700 font-mono text-sm">₹ {(payDialog.eligible_amount || payDialog.total_amount || 0).toLocaleString('en-IN')}</span></p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paid Date *</label>
                  <input
                    type="date"
                    value={paymentData.paid_date}
                    onChange={(e) => setPaymentData({ ...paymentData, paid_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold"
                    data-testid="input-paid-date"
                    required
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Payment Mode *</label>
                  <select
                    value={paymentData.payment_mode}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold bg-white"
                    data-testid="select-payment-mode"
                  >
                    <option value="Bank Transfer">Bank Transfer (NEFT/RTGS/IMPS)</option>
                    <option value="UPI">UPI</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Transaction / Ref Number *</label>
                  <input
                    type="text"
                    placeholder="e.g. UTR123456789 / CHQ-456"
                    value={paymentData.transaction_ref}
                    onChange={(e) => setPaymentData({ ...paymentData, transaction_ref: e.target.value })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-mono uppercase"
                    data-testid="input-txn-ref"
                    required
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paid Amount (₹)</label>
                  <input
                    type="number"
                    value={paymentData.paid_amount}
                    onChange={(e) => setPaymentData({ ...paymentData, paid_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-mono font-bold"
                    data-testid="input-paid-amount"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Finance Remarks (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Processed via bank batch..."
                  value={paymentData.payment_remarks}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_remarks: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl p-2.5 text-xs outline-none focus:border-blue-500"
                  data-testid="input-payment-remarks"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => setPayDialog(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handlePayConfirm}
                  disabled={actionLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                  data-testid="btn-confirm-pay"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Mark Payment as Paid
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* VIEW DETAILS MODAL */}
        {viewingReport && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="finance-view-modal">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 p-6 border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-900">Local Conveyance Claim — {viewingReport.report_date}</h3>
                  <StatusBadge status={viewingReport.status} />
                </div>
                <button type="button" onClick={() => setViewingReport(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Master Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs">
                <div><span className="text-slate-400 block text-[10px]">Employee</span><b>{viewingReport.employee_name} ({viewingReport.employee_code})</b></div>
                <div><span className="text-slate-400 block text-[10px]">Company / Branch</span><b>{viewingReport.company_name} ({viewingReport.branch_name})</b></div>
                <div><span className="text-slate-400 block text-[10px]">Department</span><b>{viewingReport.department_name}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Submitted Date</span><b>{(viewingReport.submitted_at || viewingReport.created_at || '').slice(0, 10)}</b></div>
              </div>

              {/* Status Timeline History */}
              {viewingReport.history && viewingReport.history.length > 0 && (
                <div className="bg-blue-50/60 border border-blue-200 p-3.5 rounded-xl space-y-2 text-xs">
                  <span className="text-[10px] font-bold text-blue-900 uppercase tracking-wider block">Audit Timeline History</span>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto">
                    {viewingReport.history.map((h, i) => (
                      <div key={i} className="flex items-start justify-between border-b border-blue-100 last:border-0 pb-1 text-[11px]">
                        <div>
                          <span className="font-bold text-blue-950">[{h.action}]</span> <span className="text-slate-700">by {h.performed_by_name} ({h.role})</span>
                          {h.remarks && <p className="text-slate-500 italic mt-0.5">{h.remarks}</p>}
                        </div>
                        <span className="text-slate-400 text-[10px] shrink-0 font-mono">{(h.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection / Payment Detail Banners */}
              {viewingReport.status === 'REJECTED' && viewingReport.rejection_reason && (
                <div className="bg-rose-50 border border-rose-300 p-3.5 rounded-xl text-xs text-rose-950 font-medium">
                  <p className="font-bold text-rose-900">Rejection Reason:</p>
                  <p className="mt-0.5">{viewingReport.rejection_reason}</p>
                </div>
              )}

              {viewingReport.status === 'PAID' && (
                <div className="bg-emerald-50 border border-emerald-300 p-3.5 rounded-xl text-xs text-emerald-950 space-y-1 font-medium">
                  <p className="font-bold text-emerald-900">Payment Completed Details:</p>
                  <p>Paid Date: <b>{viewingReport.paid_date}</b> &middot; Mode: <b>{viewingReport.payment_mode}</b> &middot; Ref: <b>{viewingReport.transaction_ref}</b> &middot; Paid Amount: <b>₹ {(viewingReport.paid_amount || 0).toLocaleString('en-IN')}</b></p>
                  {viewingReport.payment_remarks && <p className="text-emerald-700 italic">Remarks: {viewingReport.payment_remarks}</p>}
                </div>
              )}

              {/* Route & Travel Summary Card */}
              <div className="space-y-3 bg-slate-50/80 p-4 rounded-xl border border-slate-200 text-xs" data-testid="finance-route-summary">
                <h4 className="font-bold uppercase text-indigo-900 tracking-wider flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-indigo-600" /> Travel Route &amp; Destinations
                </h4>

                <div className="bg-white p-3.5 rounded-xl border border-indigo-100 shadow-sm space-y-2">
                  <span className="text-[10px] font-extrabold uppercase text-indigo-700 tracking-wide block">Connected Route Timeline</span>
                  <p className="font-mono font-bold text-slate-900 text-sm">
                    {[viewingReport.from_location, viewingReport.to_location, ...(viewingReport.additional_destinations || [])].filter(Boolean).join(' → ') || 'Local Travel Route'}
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-slate-100 text-[11px] text-slate-700">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Travel Mode</span>
                      <strong className="text-slate-900">{viewingReport.travel_mode || 'Cab'}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Vehicle Number</span>
                      <strong className="text-slate-900 uppercase font-mono">{viewingReport.vehicle_number || 'N/A'}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Odometer Readout</span>
                      <strong className="text-slate-900 font-mono">{viewingReport.start_odometer || 0} &rarr; {viewingReport.end_odometer || 0}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Overall Total KM</span>
                      <strong className="text-indigo-700 font-mono text-xs">{viewingReport.total_km || 0} KM</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Segment-Wise Breakdown Table */}
              {viewingReport.segments && viewingReport.segments.length > 0 && (
                <div className="space-y-2 text-xs" data-testid="finance-segment-table">
                  <h4 className="font-bold uppercase text-slate-700 tracking-wider">Segment-Wise Conveyance Breakdown</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 font-bold text-slate-700 border-b">
                        <tr>
                          <th className="px-3 py-2">Seg #</th>
                          <th className="px-3 py-2">From Location</th>
                          <th className="px-3 py-2">To Location</th>
                          <th className="px-3 py-2">Mode</th>
                          <th className="px-3 py-2 text-right">Distance (KM)</th>
                          <th className="px-3 py-2 text-right">Cost (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {viewingReport.segments.map((s, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 font-mono font-bold text-indigo-700">#{idx + 1}</td>
                            <td className="px-3 py-2">{s.from_location}</td>
                            <td className="px-3 py-2 font-semibold">{s.to_location}</td>
                            <td className="px-3 py-2 text-slate-600">{s.travel_mode || viewingReport.travel_mode}</td>
                            <td className="px-3 py-2 text-right font-mono">{s.distance_km || 0} KM</td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">₹ {(parseFloat(s.conveyance_cost) || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Expense Totals */}
              <div className="bg-slate-900 text-white rounded-xl p-4 text-xs space-y-3 font-sans" data-testid="finance-expense-summary">
                <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wider block">Expense Category Breakdown &amp; Grand Total</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
                  <div className="bg-slate-800 p-2.5 rounded-lg border border-slate-700"><span className="text-slate-400 block text-[10px]">1. Travel &amp; Conveyance</span><strong className="text-white">₹ {(viewingReport.conveyance_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                  <div className="bg-slate-800 p-2.5 rounded-lg border border-slate-700"><span className="text-slate-400 block text-[10px]">2. Food &amp; Refreshments</span><strong className="text-white">₹ {(viewingReport.food_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                  <div className="bg-slate-800 p-2.5 rounded-lg border border-slate-700"><span className="text-slate-400 block text-[10px]">3. Parking &amp; Toll</span><strong className="text-white">₹ {(viewingReport.parking_toll_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                  <div className="bg-slate-800 p-2.5 rounded-lg border border-slate-700"><span className="text-slate-400 block text-[10px]">4. Other Expenses</span><strong className="text-white">₹ {(viewingReport.other_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-slate-800/90 p-3 rounded-lg border border-slate-700 gap-2 font-mono">
                  <div>
                    <span className="text-amber-400 font-bold block text-xs uppercase">Grand Total Claim Amount</span>
                    <span className="text-[10px] text-slate-400">Sum of all 4 expense categories</span>
                  </div>
                  <span className="text-amber-400 font-black text-lg">
                    ₹ {((viewingReport.conveyance_amount || 0) + (viewingReport.food_amount || 0) + (viewingReport.parking_toll_amount || 0) + (viewingReport.other_amount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => downloadPdf(viewingReport.id)}>
                  <Download className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <Button type="button" onClick={() => setViewingReport(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
